import { ComponentProps, useState } from 'react';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import HelpModalImage from '../../images/HelpModalImage';
import { HelpContent as CpgHelpContent } from '@features/glossary/subdomains/cpg';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import './styles/help_modal.scss';
import { ForwardRefIcon } from '@components/shared/icons/ForwardRefIcon';
import BaseDialog from '@components/shared/Dialog/BaseDialog';
import { HelpContent as VisitationHelpContent } from '@features/glossary/subdomains/visitation';

type HelpModalOpenerProps = {
  openModal: () => void;
  sx: ComponentProps<typeof Box>['sx'];
};

export const HelpModalOpener = (props: HelpModalOpenerProps) => {
  const iconButtonStyles = {
    display: { sm: 'none', md: 'flex' },
    ...props.sx,
  };

  const icon = (
    <IconButton
      size="large"
      id="help_modal_link"
      aria-label="help"
      sx={iconButtonStyles}
      onClick={props.openModal}
    >
      <ForwardRefIcon icon={faQuestion} />
    </IconButton>
  );

  return icon;
};

const HelpModalTitle = () => (
  <Box id="help-modal-title">
    <Box id="help-img">
      <HelpModalImage />
      <Typography variant="h3">Help</Typography>
    </Box>
    <Box id="help-help-text">
      <Typography>
        Need something explained? Having trouble? Found bug?
      </Typography>
      <Typography>
        Please contact us anytime at{' '}
        <a href="mailto:lcihotline@inmarket.com">lcihotline@inmarket.com</a>
      </Typography>
    </Box>
  </Box>
);

const contentOptions: {
  title: string;
  body: React.ReactNode;
  type: HelpModalProps['defaultType'];
}[] = [
  {
    title: 'Visitation',
    body: <VisitationHelpContent />,
    type: 'visitation',
  },
  {
    title: 'CPG',
    body: <CpgHelpContent />,
    type: 'cpg',
  } as const,
];

export type HelpModalProps = {
  closeModal: () => void;
  isOpen: boolean;
  defaultType?: 'visitation' | 'cpg' | 'rsl';
};

export const HelpModal = (props: HelpModalProps) => {
  const [helpModalType, setHelpModalType] = useState<
    NonNullable<HelpModalProps['defaultType']>
  >(props.defaultType ?? 'visitation');
  const content = contentOptions.find(option => option.type === helpModalType);

  return (
    <BaseDialog
      open={props.isOpen}
      onClose={props.closeModal}
      title={<HelpModalTitle />}
      className="help-modal"
      id="help_modal"
    >
      <Stack direction="column" gap="1rem" padding="1rem">
        {contentOptions.length > 1 ? (
          <FormControl>
            <InputLabel id="help-modal-type">Campaign Type</InputLabel>
            <Select
              label="Campaign Type"
              labelId="help-modal-type"
              value={helpModalType}
              onChange={e =>
                setHelpModalType(e.target.value as typeof helpModalType)
              }
            >
              {contentOptions.map(option => (
                <MenuItem key={option.type} value={option.type}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        {content?.body ?? null}
      </Stack>
    </BaseDialog>
  );
};
